import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { Language, Psychology, School, Build, Group } from '@mui/icons-material';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 4,
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: 4,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderColor: 'primary.main',
        },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          p: 2,
          borderRadius: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #FF6B00, #FF8E3C)',
          boxShadow: '0 8px 16px rgba(255, 107, 0, 0.2)',
        }}
      >
        <Icon sx={{ fontSize: 32, color: '#fff' }} />
      </Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#fff' }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
        {description}
      </Typography>
    </Paper>
  </motion.div>
);

const PartnerLogo = ({ partner, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    viewport={{ once: true }}
  >
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <motion.img
        src={partner.img}
        alt={partner.name}
        style={{
          maxWidth: '100%',
          maxHeight: 60,
          objectFit: 'contain',
          filter: 'brightness(0.9)',
        }}
        whileHover={{ 
          scale: 1.05,
          filter: 'brightness(1)'
        }}
        transition={{ duration: 0.2 }}
      />
    </Box>
  </motion.div>
);

const WhyUs = ({ partners }) => {
  const features = [
    {
      icon: Language,
      title: 'Local Expertise, Global Standards',
      description: 'Deep understanding of the Burundian market combined with implementation of international best practices.',
    },
    {
      icon: Psychology,
      title: 'Innovation-Driven Approach',
      description: 'Cutting-edge digital solutions with creative problem-solving and continuous adaptation to market trends.',
    },
    {
      icon: School,
      title: 'Educational Impact',
      description: 'Commitment to skill development and knowledge transfer to local talent, investing in community growth.',
    },
    {
      icon: Build,
      title: 'Comprehensive Solutions',
      description: 'End-to-end digital services with integrated marketing strategies customized for each client.',
    },
    {
      icon: Group,
      title: 'Young and Dynamic Team',
      description: 'Fresh perspectives and agile methodology driven by a passion for digital transformation.',
    },
  ];

  return (
    <Box
      id="why-us"
      component="section"
      sx={{
        py: 8,
        backgroundColor: '#0a0a0a',
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 6,
              textAlign: 'center',
              color: 'common.white',
              fontWeight: 700,
            }}
          >
            Why Choose Us
          </Typography>
        </motion.div>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard {...feature} delay={index * 0.1} />
            </Grid>
          ))}
        </Grid>

        {partners && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: 4,
                  color: 'rgba(200, 200, 200, 1)',
                  fontWeight: 700,
                }}
              >
                Trusted Partners
              </Typography>
            </motion.div>

            <Grid container spacing={2} alignItems="center" justifyContent="center">
              {partners.map((partner, index) => (
                <Grid item xs={6} sm={4} md={3} key={partner.name || index}>
                  <PartnerLogo partner={partner} index={index} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default WhyUs;
